import React, { useEffect, useState, useRef } from 'react';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import { Button, Segment, Message, Loader } from 'semantic-ui-react';
import Toast from '@plone/volto/components/manage/Toast/Toast';
import { 
  createPeachCheckout, 
  getPeachCheckoutStatus,
  processPeachPayment,
  resetPeachCheckout,
} from '../reducers/peachPayment';
// import './PeachPaymentCheckout.css';

const PeachPaymentCheckout = ({
  amount,
  currency = 'ZAR',
  subscriptionType,
  onPaymentSuccess,
  onPaymentError,
}) => {
  const dispatch = useDispatch();
  const checkoutRef = useRef(null);
  
  // Redux state selectors - now from peachPayment reducer
  const peachCheckout = useSelector((state) => state.peachPayment?.checkout);
  const peachStatus = useSelector((state) => state.peachPayment?.status);
  const peachProcess = useSelector((state) => state.peachPayment?.process);
  
  const [scriptLoaded, setScriptLoaded] = useState(false);
  const [paymentComplete, setPaymentComplete] = useState(false);
  const [paymentSuccessful, setPaymentSuccessful] = useState(false);

  // Reset on unmount
  useEffect(() => {
    return () => {
      dispatch(resetPeachCheckout());
    };
  }, [dispatch]);

  // Load Peach Payments script when checkout ID is available
  useEffect(() => {
    if (peachCheckout?.checkoutId && !document.getElementById('peach-script')) {
      const script = document.createElement('script');
      script.id = 'peach-script';
      script.src = "https://checkout.peachpayments.com/js/checkout.js";
      script.onload = () => {
        console.log('Peach Payments script loaded');
        setScriptLoaded(true);
      };
      script.onerror = () => {
        console.error('Failed to load Peach Payments script');
        toast.error(
          <Toast
            error
            title="Script Error"
            content="Failed to load payment system"
          />
        );
      };
      document.body.appendChild(script);
    }
  }, [peachCheckout?.checkoutId]);

  // Initialize and render checkout when script is loaded
  useEffect(() => {
    if (peachCheckout?.checkoutId && scriptLoaded && window.Checkout) {
      console.log('Initializing Peach Payments checkout with:', {
        entityId: peachCheckout.entityId,
        checkoutId: peachCheckout.checkoutId
      });
      
      try {
        const checkout = window.Checkout.initiate({
          key: peachCheckout.entityId,
          checkoutId: peachCheckout.checkoutId,
          events: {
            onCompleted: (event) => {
              console.log('Payment completed:', event);
              // Get the payment status from your backend
              dispatch(getPeachCheckoutStatus(peachCheckout.checkoutId));
            },
            onCancelled: (event) => {
              console.log('Payment cancelled:', event);
              toast.info(
                <Toast
                  info
                  title="Payment Cancelled"
                  content="You cancelled the payment"
                />
              );
            },
            onExpired: (event) => {
              console.log('Checkout expired:', event);
              toast.warning(
                <Toast
                  warning
                  title="Session Expired"
                  content="Your payment session has expired. Please try again."
                />
              );
            },
            onError: (event) => {
              console.error('Checkout error:', event);
              toast.error(
                <Toast
                  error
                  title="Payment Error"
                  content="An error occurred during payment"
                />
              );
            },
          },
          customisations: {
            card: {
              showBillingFields: false
            }
          },
          options: {
            ordering: {
              CARD: 1
            },
            paymentMethods: {
              include: ["CARD"]
            },
            theme: {
              brand: {
                primary: "#ff0000",
              },
              cards: {
                background: "#00ff00",
                backgroundHover: "#F3F3F4",
              }
            },
          },
        });
        
        console.log('Rendering checkout to #payment-form');
        checkout.render("#payment-form");
        
        // Store checkout instance in ref if you need to interact with it later
        checkoutRef.current = checkout;
        
      } catch (error) {
        console.error('Error initializing checkout:', error);
        toast.error(
          <Toast
            error
            title="Initialization Error"
            content={`Failed to initialize payment: ${error.message}`}
          />
        );
      }
    }
  }, [peachCheckout?.checkoutId, peachCheckout?.entityId, scriptLoaded, dispatch]);

  // Watch for payment status results
  useEffect(() => {
    console.log('Status check useEffect triggered:', {
      statusLoaded: peachStatus?.loaded,
      statusCode: peachStatus?.status,
      error: peachStatus?.error,
      checkoutId: peachCheckout?.checkoutId,
      subscriptionType: subscriptionType
    });
    
    if (peachStatus?.loaded && peachStatus?.status) {
      const code = peachStatus.status;
      console.log('Payment status code:', code);
      setPaymentComplete(true);
      
      if (
        code.match(/^(000\.000\.|000\.100\.1|000\.[36])/) ||
        code === '000.000.000'
      ) {
        setPaymentSuccessful(true);
        
        // Notify backend to process the successful payment
        if (peachCheckout?.checkoutId && subscriptionType) {
          console.log('Payment successful! Calling processPeachPayment with:', {
            checkoutId: peachCheckout.checkoutId,
            subscriptionType,
            payment: peachStatus.payment
          });
          
          dispatch(processPeachPayment(
            peachCheckout.checkoutId,
            subscriptionType,
            peachStatus.payment
          ));
          
        } else {
          console.error('Missing required data for processPeachPayment:', {
            checkoutId: peachCheckout?.checkoutId,
            subscriptionType
          });
        }
        
        toast.success(
          <Toast
            success
            title="Payment Successful"
            content="Your subscription has been activated!"
          />
        );
        if (onPaymentSuccess) {
          onPaymentSuccess(peachStatus.payment);
        }
      } else {
        setPaymentSuccessful(false);
        toast.error(
          <Toast
            error
            title="Payment Failed"
            content={peachStatus.description || 'Payment was not successful'}
          />
        );
        if (onPaymentError) {
          onPaymentError(peachStatus);
        }
      }
    }
    
    if (peachStatus?.error) {
      setPaymentComplete(true);
      setPaymentSuccessful(false);
      toast.error(
        <Toast
          error
          title="Error"
          content={`Failed to verify payment: ${peachStatus.error.message || 'Unknown error'}`}
        />
      );
      if (onPaymentError) {
        onPaymentError(peachStatus.error);
      }
    }
  }, [peachStatus, onPaymentSuccess, onPaymentError, dispatch, peachCheckout?.checkoutId, subscriptionType]);

  // Watch for successful backend processing and refresh page
  useEffect(() => {
    if (peachProcess?.loaded && peachProcess?.success) {
      console.log('Backend processing complete, refreshing page in 2 seconds...');
      
      // Optional: Show a brief message before refresh
      toast.success(
        <Toast
          success
          title="Success"
          content="Subscription activated! Refreshing page..."
        />
      );
      
      // Option A: Refresh current page
      // setTimeout(() => {
      //   window.location.reload();
      // }, 2000);
      
      // Option B: Redirect to specific page (uncomment and replace URL)
      setTimeout(() => {
        window.location.href = '/personal-information';  // or wherever you want
      }, 2000);
      
      // Option C: Use React Router navigation (if you're using it)
      /*
      setTimeout(() => {
        history.push('/dashboard');
      }, 2000);
      */
    }
    
    if (peachProcess?.error) {
      console.error('Backend processing failed:', peachProcess.error);
      toast.error(
        <Toast
          error
          title="Activation Error"
          content="Payment verified but subscription activation failed. Please contact support."
        />
      );
    }
  }, [peachProcess]);

  // Initialize checkout using Redux action
  const initializeCheckout = () => {
    dispatch(createPeachCheckout(amount, currency, subscriptionType));
  };

  // Show loading state
  if (peachCheckout?.loading) {
    return (
      <Segment>
        <Loader active>Initializing payment...</Loader>
      </Segment>
    );
  }

  // Show error state
  if (peachCheckout?.error) {
    return (
      <Message negative>
        <Message.Header>Payment Initialization Failed</Message.Header>
        <p>{peachCheckout.error?.type || 'Unknown type'}</p>
        <p>{peachCheckout.error.message || 'Unknown error'}</p>
        <Button onClick={initializeCheckout}>Try Again</Button>
      </Message>
    );
  }

  // Show initial state
  if (!peachCheckout?.checkoutId) {
    return (
      <Button primary size="large" onClick={initializeCheckout}>
        Activate
      </Button>
    );
  }

  // Show payment form
  return (
    <Segment>
      {peachStatus?.loading && (
        <Message info>
          <Message.Header>Processing Payment</Message.Header>
          <p>Please wait while we verify your payment...</p>
        </Message>
      )}
      
      {/* Show success message when payment is complete and successful */}
      {paymentComplete && paymentSuccessful && (
        <Message positive>
          <Message.Header>Payment Successful!</Message.Header>
          <p>Your subscription has been activated. Thank you for your payment.</p>
          {peachStatus?.payment && (
            <div style={{ marginTop: '1rem', fontSize: '0.9em' }}>
              <p><strong>Transaction ID:</strong> {peachStatus.payment.id}</p>
              <p><strong>Amount:</strong> {peachStatus.payment.amount} {peachStatus.payment.currency}</p>
            </div>
          )}
          <Button 
            primary 
            onClick={() => window.location.href = '/success-page'} 
            style={{ marginTop: '1rem' }}
          >
            Continue
          </Button>
        </Message>
      )}
      
      {/* Show failure message when payment is complete but failed */}
      {paymentComplete && !paymentSuccessful && (
        <Message negative>
          <Message.Header>Payment Failed</Message.Header>
          <p>{peachStatus?.description || 'Your payment was not successful. Please try again.'}</p>
          <Button onClick={initializeCheckout} style={{ marginTop: '1rem' }}>
            Try Again
          </Button>
        </Message>
      )}
      
      {/* Only show payment form if payment is not complete */}
      {!paymentComplete && (
        <div id="payment-form" className="peach-payment-container" style={{ minHeight: '360px' }}></div>
      )}
    </Segment>
  );
};

PeachPaymentCheckout.propTypes = {
  amount: PropTypes.number.isRequired,
  currency: PropTypes.string,
  subscriptionType: PropTypes.string.isRequired,
  onPaymentSuccess: PropTypes.func,
  onPaymentError: PropTypes.func,
};

export default PeachPaymentCheckout;
